import { useOutletContext } from 'react-router-dom';
import {
  BanknotesIcon,
  BoltIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon,
  UsersIcon,
} from '@heroicons/react/24/solid';

import AnalyticsTable from '../../../components/AiSpend/AnalyticsTable';
import BarChart from '../../../components/Charts/BarChart';
import ChartCard from '../../../components/Charts/ChartCard';
import DoughnutChart from '../../../components/Charts/DoughnutChart';
import LineChart from '../../../components/Charts/LineChart';
import UsageCell from '../../../components/AiSpend/UsageCell';
import ErrorState from '../../../components/DataDisplay/ErrorState';
import StatCard from '../../../components/DataDisplay/StatCard';
import { getAiSpendOverview } from '../../../services/aiSpendService';
import {
  formatCount,
  formatLatency,
  formatMoney,
  formatPercent,
  formatUsage,
} from '../../../utils/helpers/aiSpendFormat';
import useApiResource from '../../../utils/hooks/useApiResource';
import { AI_SPEND_BASE } from './aiSpendPages';

const topColumns = (linkPrefix) => [
  { key: 'label', label: 'Name', render: (row) => row.label },
  {
    key: 'cost_usd',
    label: 'Cost',
    render: (row) => formatMoney(row.cost_usd),
    className: 'font-semibold',
  },
  { key: 'calls', label: 'Calls', render: (row) => formatCount(row.calls) },
  {
    key: 'usage',
    label: 'Usage',
    render: (row) => <UsageCell usage={row.usage} billingUnit={row.billing_unit} />,
  },
];

export default function AiSpendOverview() {
  const { queryParams, queryKey } = useOutletContext();
  const { data, error, loading, refetch } = useApiResource(
    ({ signal }) => getAiSpendOverview({ params: queryParams, signal }),
    { deps: [queryKey] },
  );

  if (error) return <ErrorState message={error} onRetry={refetch} />;

  const totals = data?.totals || {};
  const previous = data?.previous_totals || {};
  const previousLabel = data
    ? `vs ${data.previous_range.date_from} – ${data.previous_range.date_to}`
    : '';

  return (
    <div className="space-y-6">
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard
          icon={BanknotesIcon}
          tone="blue"
          label="Total spend"
          value={formatMoney(totals.cost_usd)}
          rawValue={totals.cost_usd}
          previousValue={previous.cost_usd}
          detail={previousLabel}
          loading={loading}
        />
        <StatCard
          icon={BoltIcon}
          tone="slate"
          label="Calls"
          value={formatCount(totals.calls)}
          rawValue={totals.calls}
          previousValue={previous.calls}
          detail={previousLabel}
          loading={loading}
        />
        <StatCard
          icon={ExclamationTriangleIcon}
          tone="red"
          label="Failed calls"
          value={formatCount(totals.failed_calls)}
          rawValue={totals.failed_calls}
          previousValue={previous.failed_calls}
          detail={
            totals.success_rate != null
              ? `${formatPercent(totals.success_rate)} succeeded`
              : ''
          }
          loading={loading}
          to={`${AI_SPEND_BASE}/errors`}
        />
        <StatCard
          icon={QuestionMarkCircleIcon}
          tone="amber"
          label="Unpriced calls"
          value={formatCount(totals.unpriced_calls)}
          rawValue={totals.unpriced_calls}
          previousValue={previous.unpriced_calls}
          detail="Cost unknown, not zero"
          loading={loading}
        />
        <StatCard
          icon={UsersIcon}
          tone="violet"
          label="Students"
          value={formatCount(totals.distinct_subjects)}
          rawValue={totals.distinct_subjects}
          previousValue={previous.distinct_subjects}
          deltaDirection="up-is-good"
          detail={`avg latency ${formatLatency(totals.avg_latency_ms)}`}
          loading={loading}
        />
      </section>

      {totals.estimated_cost_share > 0 && (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
          {formatPercent(totals.estimated_cost_share)} of this figure comes from rates
          marked as estimates. Confirm them against a provider invoice before treating
          the total as exact.
        </p>
      )}

      <section>
        <h2 className="mb-2 text-lg font-semibold text-slate-900 dark:text-white">
          Spend by billing unit
        </h2>
        <p className="mb-3 text-sm text-slate-500 dark:text-slate-400">
          Each provider bills in its own unit. Only the cost column is comparable
          between rows; the quantities are deliberately never totalled.
        </p>
        <AnalyticsTable
          loading={loading}
          rows={data?.usage_by_unit || []}
          rowKey={(row) => row.unit}
          emptyTitle="No AI calls in this period"
          columns={[
            { key: 'unit', label: 'Billing unit', render: (row) => row.unit.replace('_', ' ') },
            {
              key: 'quantity',
              label: 'Quantity',
              render: (row) => formatUsage([{ unit: row.unit, quantity: row.quantity }]),
            },
            { key: 'calls', label: 'Calls', render: (row) => formatCount(row.calls) },
            {
              key: 'cost_usd',
              label: 'Cost (the only comparable column)',
              render: (row) => formatMoney(row.cost_usd),
              className: 'font-semibold',
            },
            {
              key: 'unpriced_calls',
              label: 'Unpriced',
              render: (row) =>
                row.unpriced_calls ? (
                  <span className="text-amber-700 dark:text-amber-300">
                    {formatCount(row.unpriced_calls)}
                  </span>
                ) : (
                  '0'
                ),
            },
          ]}
        />
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ChartCard
            title="Daily spend"
            subtitle={`Buckets are calendar days in ${data?.timezone || 'the reporting timezone'}`}
            loading={loading}
            isEmpty={!loading && !(data?.daily || []).some((day) => day.cost_usd)}
          >
            <LineChart
              ariaLabel="Daily AI spend"
              labels={(data?.daily || []).map((day) => day.label)}
              datasets={[
                {
                  key: 'cost',
                  label: 'Cost',
                  data: (data?.daily || []).map((day) => day.cost_usd || 0),
                },
              ]}
            />
          </ChartCard>
        </div>
        <ChartCard
          title="Cost share by provider"
          subtitle="Built from the provider breakdown, not from a top-models list"
          loading={loading}
          isEmpty={!loading && (data?.by_provider || []).length === 0}
        >
          <DoughnutChart
            ariaLabel="Cost share by provider"
            segments={(data?.by_provider || []).map((row) => ({
              key: row.key,
              label: row.label,
              value: row.cost_usd || 0,
            }))}
          />
        </ChartCard>
      </section>

      <ChartCard
        title="Cost by feature"
        loading={loading}
        isEmpty={!loading && (data?.by_feature || []).length === 0}
      >
        <BarChart
          horizontal
          ariaLabel="Cost by feature"
          labels={(data?.by_feature || []).map((row) => row.label)}
          datasets={[
            {
              key: 'cost',
              label: 'Cost',
              data: (data?.by_feature || []).map((row) => row.cost_usd || 0),
            },
          ]}
        />
      </ChartCard>

      <div className="grid gap-6 lg:grid-cols-2">
        <section>
          <h2 className="mb-2 text-lg font-semibold text-slate-900 dark:text-white">
            By provider
          </h2>
          <AnalyticsTable
            loading={loading}
            rows={data?.by_provider || []}
            columns={topColumns()}
          />
        </section>
        <section>
          <h2 className="mb-2 text-lg font-semibold text-slate-900 dark:text-white">
            By feature
          </h2>
          <AnalyticsTable
            loading={loading}
            rows={data?.by_feature || []}
            columns={topColumns()}
          />
        </section>
        <section>
          <h2 className="mb-2 text-lg font-semibold text-slate-900 dark:text-white">
            Top models
          </h2>
          <AnalyticsTable
            loading={loading}
            rows={data?.top_models || []}
            columns={topColumns()}
          />
        </section>
        <section>
          <h2 className="mb-2 text-lg font-semibold text-slate-900 dark:text-white">
            Top organizations
          </h2>
          <AnalyticsTable
            loading={loading}
            rows={data?.top_organizations || []}
            columns={topColumns()}
          />
        </section>
      </div>
    </div>
  );
}
